import type { ComboboxOptionsProps } from '@headlessui/react';
import type { ReactNode } from 'react';
import type { DropdownOption } from '../DropdownOption/DropdownOption';

import { ComboboxOptions } from '@headlessui/react';
import { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import DropdownOptionWrapper from '../DropdownOption/DropdownOption';

interface ComboboxOptionsWrapperProps extends ComboboxOptionsProps {
  isFilterDropdown?: boolean;
  additionalDropdownClasses?: string;
  testId?: string;
}

const ComboboxOptionsWrapper = ({
  isFilterDropdown,
  children,
  anchor,
  additionalDropdownClasses,
  testId,
  ...props
}: ComboboxOptionsWrapperProps) => {
  // Multiple-mode chips can shrink the input; anchor the fallback to the full control instead.
  const positionRelativeToControl = anchor === false;

  return (
    <ComboboxOptions
      className={twMerge(
        '!max-h-40 rounded-md overflow-auto',
        'min-w-40 w-(--button-width) max-w-(--button-width)',
        'transition-[opacity,translate]',
        'scrollbar-sm bg-white dark:bg-slate-900 dark:border dark:border-slate-800 text-base shadow-md',
        'focus:outline-hidden z-20 ease-in duration-100 data-closed:opacity-0',
        'py-px', // when virtual scrolling is set, without set padding/height the options are not visible
        isFilterDropdown &&
          'shadow-none border-1 border-slate-200 !max-h-60 min-w-min max-w-max dark:bg-slate-800 dark:border-slate-700',
        positionRelativeToControl && 'absolute left-0 top-full mt-5',
        additionalDropdownClasses,
      )}
      as="ul"
      modal={false}
      anchor={
        anchor === false
          ? false
          : anchor || {
              to: 'bottom start',
              gap: isFilterDropdown ? 12 : 20,
              padding: 16,
              offset: -16,
            }
      }
      {...getTestProps(testId, 'options')}
      {...props}
    >
      {children}
    </ComboboxOptions>
  );
};

interface DropdownOptionsProps extends ComboboxOptionsProps {
  handleAddOnEmpty?: ReactNode;
  groupedOptions?: { [key: string]: DropdownOption[] };
  renderWithoutOptions?: ReactNode;
  isVirtualScrolling?: boolean;
  isFilterDropdown?: boolean;
  additionalDropdownClasses?: string;
  additionalOptionsClasses?: string;
  testId?: string;
}

const DropdownOptions = ({
  handleAddOnEmpty,
  groupedOptions,
  renderWithoutOptions,
  isVirtualScrolling,
  isFilterDropdown,
  additionalDropdownClasses,
  additionalOptionsClasses,
  testId,
  ...props // headless ui passed props to combobox options
}: DropdownOptionsProps) => {
  if (isVirtualScrolling) {
    return (
      <ComboboxOptionsWrapper
        isFilterDropdown={isFilterDropdown}
        additionalDropdownClasses={additionalDropdownClasses}
        testId={testId}
        {...props}
      >
        {({ option }) => (
          <DropdownOptionWrapper
            option={option}
            isVirtualScrolling={isVirtualScrolling}
            isFilterDropdown={isFilterDropdown}
            additionalOptionsClasses={additionalOptionsClasses}
            testId={testId}
          />
        )}
      </ComboboxOptionsWrapper>
    );
  }

  return (
    <ComboboxOptionsWrapper
      isFilterDropdown={isFilterDropdown}
      additionalDropdownClasses={additionalDropdownClasses}
      testId={testId}
      {...props}
    >
      {renderWithoutOptions ||
        Object.keys(groupedOptions || {}).map((groupName) => (
          <Fragment key={groupName}>
            {groupName && (
              <li
                className={twMerge(
                  'italic font-bold py-2 pl-4 pr-9 dark:text-slate-200',
                )}
                key={groupName}
                {...getTestProps(testId, `${groupName}-group`)}
              >
                {groupName}
              </li>
            )}
            <>
              {handleAddOnEmpty}
              {groupedOptions?.[groupName]?.map((option) => (
                <DropdownOptionWrapper
                  key={option.value}
                  option={option}
                  isFilterDropdown={isFilterDropdown}
                  additionalOptionsClasses={additionalOptionsClasses}
                  testId={testId}
                />
              ))}
            </>
          </Fragment>
        ))}
    </ComboboxOptionsWrapper>
  );
};

export default DropdownOptions;
